/**
 * Block type schema and content shapes for the NOS block editor.
 *
 * Used by pages, blog posts, and event descriptions. ONE editing pattern for all content.
 * Extends the existing page_sections table (section_type + content JSONB).
 *
 * @see docs/specs/MULTI_PAGE_WEBSITE_BUILDER.md
 */

// ─── Block-level style overrides (NOS-119) ───────────────────────────────────

/**
 * Optional per-block style overrides stored in content._styles.
 * Used for import fidelity and contextual editing — persists through editing
 * and is extracted during site cloning. Any block's content may include this.
 */
export interface BlockStyleOverrides {
  corner_radius?: "none" | "sm" | "md" | "lg" | "full"
  padding?: "compact" | "normal" | "spacious"
  /** Hex color or "transparent" */
  background?: string
  /** Hex color for text */
  text_color?: string
  text_align?: "left" | "center" | "right"
  /** NOS-154: section width — "contained" constrains to max-w-5xl mx-auto; absent = full width (current behavior) */
  width?: "contained"
  /** CSS class names to apply to this element */
  custom_class?: string
}

// ─── Block type identifiers ────────────────────────────────────────────────

/** All supported block types. Add new types here when extending the system. */
export const BLOCK_TYPES = [
  "hero",
  "impact_stats",
  "donation_cta",
  "testimonials",
  "volunteer_signup",
  "gallery",
  "contact",
  "footer",
  "paragraph",
  "heading",
  "image",
  "team_members",
  "timeline",
  "form",
  "accordion",
  "cards",
  "logo_grid",
  "table",
  "fundraising_goal",
  "list",
  "blockquote",
  "video",
  "callout",
  "image_text",
  "pull_quote",
  "divider",
  "button",
  "file_download",
  "embed",
  "newsletter_signup",
  "author_bio",
  "map",
  "donation_form",
  "campaign_banner",
  "latest_posts",
] as const

export type BlockType = (typeof BLOCK_TYPES)[number]

/** Block types that require org context in render (donation modal, org name, etc.) */
export const BLOCK_TYPES_REQUIRING_ORG: BlockType[] = ["hero", "donation_cta", "footer"]

// ─── Content shape interfaces (one per block type) ────────────────────────

export interface HeroBlockContent {
  headline?: string
  subheadline?: string
  cta_text?: string
  background_image?: string
  /** NOS-236: hero min-height — "full" (100vh), "70", "50". Absent = renderer default. */
  hero_height?: "full" | "70" | "50"
  /** NOS-236: scroll-arrow toggle. Absent = on for the homepage, off elsewhere. */
  scroll_arrow?: boolean
}

export interface ImpactStatItem {
  label: string
  value: string
}

export interface ImpactStatsBlockContent {
  heading?: string
  stats?: ImpactStatItem[]
  columns?: 1 | 2 | 3
}

export interface DonationCTABlockContent {
  heading?: string
  body?: string
  cta_text?: string
  /** NOS-236: optional secondary CTA (e.g. Volunteer next to Donate). */
  secondary_cta_text?: string
  secondary_cta_url?: string
}

export interface TeamMemberItem {
  name: string
  role: string
  bio?: string
  image_url?: string
  email?: string
  linkedin?: string
}

export interface SocialLink {
  label: string
  url: string
}

export interface FooterBlockContent {
  tagline?: string
  social_links?: SocialLink[]
}

/** TipTap JSON output for rich text. Used by paragraph block. */
export type RichTextContent = Record<string, unknown> | null

export interface ParagraphBlockContent {
  /** TipTap document JSON (ProseMirror format) — set by editor */
  content?: RichTextContent
  /** Plain text — set by importer when Claude cannot output TipTap JSON */
  text?: string
}

export interface HeadingBlockContent {
  text?: string
  level?: 1 | 2 | 3 | 4 | 5 | 6
}

export interface ImageBlockContent {
  url?: string
  alt?: string
  caption?: string
}

/** Team members block (About page) - NOS-42. Uses contacts or manual entries. */
export interface TeamMembersBlockContent {
  heading?: string
  /** Manual entries or references to contacts. */
  members?: TeamMemberItem[]
  /** Layout: grid (2-3 cols) or list (single column) */
  layout?: "grid" | "list"
  /** Grid column count when layout = "grid" */
  columns?: 2 | 3
}

/** Timeline block - NOS-43. */
export interface TimelineItem {
  year?: string
  title?: string
  description?: string
  image_url?: string
}

export interface TimelineBlockContent {
  heading?: string
  items?: TimelineItem[]
  layout?: "vertical" | "horizontal"
}

/** Contact form block - NOS-55. */
export interface FormField {
  name: string
  type: "text" | "email" | "textarea" | "select"
  label?: string
  required?: boolean
  options?: string[]
}

export interface FormBlockContent {
  heading?: string
  submit_label?: string
  fields?: FormField[]
}

/** Testimonials section - NOS-90. heading + testimonials array. */
export interface TestimonialItem {
  quote?: string
  author?: string
  role?: string
}

export interface TestimonialsBlockContent {
  heading?: string
  testimonials?: TestimonialItem[]
  columns?: 1 | 2
}

/** Volunteer Signup section - NOS-90. heading + paragraph + button. */
export interface VolunteerSignupBlockContent {
  heading?: string
  body?: string
  cta_text?: string
}

/** Gallery section - NOS-90. heading + images array. */
export interface GalleryImageItem {
  url?: string
  alt?: string
  caption?: string
}

export interface GalleryBlockContent {
  heading?: string
  images?: GalleryImageItem[]
  columns?: 1 | 2 | 3
}

/** Contact section - NOS-90. heading + paragraph + form-field. */
export interface ContactBlockContent {
  heading?: string
  body?: string
  /** Form field config; can reuse FormField shape */
  fields?: FormField[]
}

// ─── NOS-120: New publishing block types ────────────────────────────────────

export interface AccordionBlockContent {
  heading?: string
  items?: Array<{ question: string; answer: string }>
  columns?: 1 | 2
}

export interface CardsBlockContent {
  heading?: string
  subheading?: string
  items?: Array<{
    title?: string
    body?: string
    icon?: string
    image_url?: string
    cta_text?: string
    cta_url?: string
  }>
  columns?: 1 | 2 | 3
}

export interface LogoGridBlockContent {
  heading?: string
  subheading?: string
  /** NOS-238: `light` = import detected white/light artwork (invisible on light backgrounds). */
  logos?: Array<{ name: string; image_url?: string; url?: string; light?: boolean }>
  columns?: 2 | 3 | 4 | 5
  /**
   * NOS-238: row-uniform logo color treatment. Absent = auto (dark when any
   * logo is flagged `light`); "dark" forces the dark treatment; "original"
   * disables it (the user override).
   */
  logo_treatment?: "dark" | "original"
}

export interface TableBlockContent {
  heading?: string
  headers?: string[]
  rows?: string[][]
}

export interface FundraisingGoalBlockContent {
  heading?: string
  goal_amount?: number
  raised_amount?: number
  currency?: string
  label?: string
  cta_text?: string
  cta_url?: string
}

export interface ListBlockContent {
  list_type?: "unordered" | "ordered"
  items?: string[]
  heading?: string
  columns?: 1 | 2
}

export interface BlockquoteBlockContent {
  quote?: string
  attribution?: string
  source?: string
}

export interface VideoBlockContent {
  url?: string
  caption?: string
  autoplay?: boolean
}

export interface CalloutBlockContent {
  type?: "info" | "tip" | "warning" | "success"
  heading?: string
  body?: string
}

export interface ImageTextBlockContent {
  heading?: string
  body?: string
  image_url?: string
  image_position?: "left" | "right"
  cta_text?: string
  cta_url?: string
}

export interface PullQuoteBlockContent {
  quote?: string
  attribution?: string
}

export interface DividerBlockContent {
  style?: "line" | "dots" | "thick"
}

export interface ButtonBlockContent {
  text?: string
  url?: string
  variant?: "primary" | "secondary" | "outline"
  align?: "left" | "center" | "right"
}

export interface FileDownloadBlockContent {
  label?: string
  url?: string
  description?: string
  file_type?: string
  file_size?: string
}

export interface EmbedBlockContent {
  url?: string
  title?: string
  height?: number
}

export interface NewsletterSignupBlockContent {
  heading?: string
  body?: string
  cta_text?: string
  placeholder?: string
}

/** NOS-162: preset giving tier with optional impact-equivalency label. */
export interface DonationTier {
  /** Whole-dollar amount for the preset tile. */
  amount?: number
  /** Org-authored equivalency, e.g. "Feeds a family for a week". Never prefilled with real orgs' claims. */
  impact_label?: string
}

/** NOS-162: embedded on-page donation form (research §C.3). One-time gifts; opens the standard payment flow. */
export interface DonationFormBlockContent {
  heading?: string
  body?: string
  tiers?: DonationTier[]
  /** Allow a custom-amount field beside the presets. */
  allow_custom?: boolean
  /** Short trust line under the button, e.g. "Tax-deductible · Secure donation". */
  trust_text?: string
  cta_text?: string
}

/** NOS-162: slim campaign/match banner with optional deadline + goal math (research §C.6). */
export interface CampaignBannerBlockContent {
  message?: string
  /** ISO date (yyyy-mm-dd); shown as a countdown while in the future. */
  deadline?: string
  /** Static goal figures; campaign-linked progress arrives with M15 integration. */
  goal_amount?: number
  raised_amount?: number
  cta_text?: string
  cta_url?: string
  /** Visitors can dismiss for the session. */
  dismissible?: boolean
}

export interface AuthorEntry {
  name?: string
  role?: string
  bio?: string
  image_url?: string
  social_links?: Array<{ label: string; url: string }>
  is_org?: boolean
}

export interface AuthorBioBlockContent {
  /** Section heading label, e.g. "About the Author" */
  heading?: string
  /** Multiple authors/contributors (preferred) */
  authors?: AuthorEntry[]
  /** Legacy single-author fields — kept for backward compatibility */
  name?: string
  role?: string
  bio?: string
  image_url?: string
  social_links?: Array<{ label: string; url: string }>
}

export interface MapBlockContent {
  address?: string
  heading?: string
  height?: number
}

/**
 * NOS-219: dynamic latest-posts block. Renders the org's most recent published
 * blog posts live from blog_posts — content carries only display settings,
 * never copied post data (the band must not freeze at import time).
 */
export interface LatestPostsBlockContent {
  heading?: string
  /** Number of posts to show (1–12, default 3). */
  count?: number
  /** Show post cover images (default true). */
  show_images?: boolean
  /** View All link placement: beside the heading, below the grid, or hidden. Default "header". */
  view_all?: "header" | "below" | "none"
  /** View All link label (default "View All"). */
  view_all_text?: string
}

// ─── Discriminated union: BlockContent by BlockType ─────────────────────────

export type BlockContentMap = {
  hero: HeroBlockContent
  impact_stats: ImpactStatsBlockContent
  donation_cta: DonationCTABlockContent
  testimonials: TestimonialsBlockContent
  volunteer_signup: VolunteerSignupBlockContent
  gallery: GalleryBlockContent
  contact: ContactBlockContent
  footer: FooterBlockContent
  paragraph: ParagraphBlockContent
  heading: HeadingBlockContent
  image: ImageBlockContent
  team_members: TeamMembersBlockContent
  timeline: TimelineBlockContent
  form: FormBlockContent
  accordion: AccordionBlockContent
  cards: CardsBlockContent
  logo_grid: LogoGridBlockContent
  table: TableBlockContent
  fundraising_goal: FundraisingGoalBlockContent
  list: ListBlockContent
  blockquote: BlockquoteBlockContent
  video: VideoBlockContent
  callout: CalloutBlockContent
  image_text: ImageTextBlockContent
  pull_quote: PullQuoteBlockContent
  divider: DividerBlockContent
  button: ButtonBlockContent
  file_download: FileDownloadBlockContent
  embed: EmbedBlockContent
  newsletter_signup: NewsletterSignupBlockContent
  author_bio: AuthorBioBlockContent
  map: MapBlockContent
  donation_form: DonationFormBlockContent
  campaign_banner: CampaignBannerBlockContent
  latest_posts: LatestPostsBlockContent
}

export type BlockContent = BlockContentMap[BlockType]

// ─── Page section (DB row shape) ───────────────────────────────────────────

/**
 * Shape of a block as stored in page_sections table.
 * section_type maps to BlockType; content is the block-specific JSON.
 */
export interface PageSectionBlock {
  id: string
  page_id: string
  org_id: string
  section_type: BlockType
  order_index: number
  content: BlockContent | Record<string, unknown> | null
}

/** Type guard: content for a given block type */
export function getBlockContent<T extends BlockType>(
  block: { section_type: string; content: unknown },
  type: T
): BlockContentMap[T] | null {
  if (block.section_type !== type) return null
  return (block.content ?? {}) as BlockContentMap[T]
}
